import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useError, useSuccess } from '@pelicargo/ui';
import { Show } from '@pelicargo/ui';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { Location, LocationAutocomplete } from '../Autocomplete/LocationAutocomplete';
import { AirportForm } from '../Forms/AirportForm';
import { SimpleForm } from '../SimpleForm';

const schema = z.object({
  id: z.string().min(1, 'Required'),
  name: z.string().min(1, 'Required'),
  latitude: z.coerce.number(),
  longitude: z.coerce.number(),
  timeZone: z.string().min(1, 'Required'),
  address: z.object({
    recipient: z.string().nullish(),
    line_one: z.string().nullish(),
    line_two: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zip_code: z.string().nullish(),
    country: z.string().nullish(),
  }),
  providers: z.array(z.string()),
});

type Props = {
  airportId?: string;
  isOpen: boolean;
  onClose: () => void;
};

const defaultValues = {
  id: '',
  name: '',
  latitude: 0,
  longitude: 0,
  timeZone: '',
  address: {
    recipient: '',
    line_one: '',
    line_two: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
  },
  providers: [],
};

export const AirportFormDrawer = ({ isOpen, onClose, airportId }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const methods = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { data } = trpc.getAirport.useQuery({ id: airportId as string }, { enabled: !!airportId });
  const { mutateAsync: createAirport, isLoading: isCreating } = trpc.createAirport.useMutation();
  const { mutateAsync: updateAirport, isLoading: isUpdating } = trpc.updateAirport.useMutation();

  useEffect(() => {
    if (!airportId) {
      methods.reset(defaultValues);
    }
    if (airportId) {
      methods.reset({
        id: data?.id,
        name: data?.name,
        timeZone: data?.time_zone,
        latitude: data?.latlng[0],
        longitude: data?.latlng[1],
        address: data?.address,
        providers: data?.providers?.map((provider) => provider?.id) ?? [],
      });
    }
  }, [data, airportId, methods]);

  const copy = useMemo(() => {
    return airportId ? 'Update Airport' : 'Create Airport';
  }, [airportId]);

  const handleLocationSelect = async (data: Location) => {
    methods.setValue('latitude', data?.latitude);
    methods.setValue('longitude', data?.longitude);
    methods.setValue('name', data?.name);
    methods.setValue('address.recipient', data?.recipient);
    methods.setValue('address.line_one', data?.lineOne);
    methods.setValue('address.city', data?.city);
    methods.setValue('address.state', data?.state);
    methods.setValue('address.zip_code', data?.zipCode);
    methods.setValue('address.country', data?.country);
    methods.setValue('timeZone', data?.timeZone);
  };

  const onValid = async (values: z.infer<typeof schema>) => {
    try {
      if (!values) throw new Error('No values provided');

      const formattedData = {
        ...values,
        latlng: [values.latitude, values.longitude],
      };

      if (airportId) {
        await updateAirport({ id: airportId, ...formattedData });
      }

      if (!airportId) {
        await createAirport(formattedData);
      }

      onClose();
      handleSuccess(airportId ? 'Airport updated' : 'Airport created');
    } catch (error) {
      handleError(error);
    }
  };

  const handleSubmit = methods.handleSubmit(onValid);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  return (
    <Drawer size="lg" placement="right" onClose={handleClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton onClick={onClose} />
        <DrawerHeader>
          <Heading as="h4" variant="h4">
            {copy}
          </Heading>
        </DrawerHeader>
        <DrawerBody>
          <SimpleForm {...methods} submitOnBlur={false}>
            <Stack>
              <LocationAutocomplete onSelect={handleLocationSelect} />
              <AirportForm />
            </Stack>
          </SimpleForm>
        </DrawerBody>
        <DrawerFooter>
          <Stack direction="row" justify={airportId ? 'space-between' : 'right'} align="center" w="full" spacing="2">
            <Show if={!!airportId}>
              <Text>ID: {airportId}</Text>
            </Show>
            <Button type="submit" colorScheme="brand" isLoading={isCreating || isUpdating} onClick={handleSubmit}>
              {copy}
            </Button>
          </Stack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
